import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AppRoute } from '@/common/app/routes';
import { BACKGROUNDS_CONFIG } from '@/common/config/media-config';
import { tournamentPrizePool } from '@/common/config/onboarding-config';
import {
  calcAdaptivePositon,
  calcAdaptiveSize,
} from '@/common/lib/adaptive-tools';
import { H1, H3, StringMain } from '@/components/Typography/Typography';
import { MainButton } from '@/components/ui/Button/Button';
import { Close } from '@/components/ui/Close/Close';
import { Picture } from '@/components/ui/Picture/Picture';
import { endWelcomeArena } from '@/redux/reducers/userReducer';
import { useAppDispatch } from '@/redux/store';

import { ModalContext } from '../Modal/config';

import { ArenaBenefits } from './ArenaBenefits';

import css from './styles.module.scss';

import wavesPng from '/img/patterns/waves-v3.png';
import wavesWebp from '/img/patterns/waves-v3.webp';
import usdPng from '/img/two-usd.png';
import usdWebp from '/img/two-usd.webp';

const WelcomeArena: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);

  const firstCircleOriginSize = { width: 201, height: 206 };
  const secondCircleOriginSize = { width: 176, height: 176 };
  const parentOrigin = { width: 380 };

  const firstCircleSize = calcAdaptiveSize({
    originSize: firstCircleOriginSize,
    parentOrigin,
  });

  const secondCircleSize = calcAdaptiveSize({
    originSize: secondCircleOriginSize,
    parentOrigin,
  });

  //base-h:top-1/2 base-h:-translate-y-1/2 top-0 translate-y-0

  return (
    <div
      style={{ height: '100vh' }}
      className="w-full flex flex-col items-center justify-end relative"
    >
      <Close
        style={{ top: 'var(--header-height)' }}
        className="absolute right-4 z-[1]"
        variant="modal"
        imageVariant="modal"
        size="lg"
        onClick={() => {
          dispatch(endWelcomeArena());
          closeModal();
        }}
      />
      <div className="flex-1 relative w-full">
        <Picture
          className="absolute left-0 bottom-0 translate-y-[34%] aspect-[642/1089] w-full"
          img={BACKGROUNDS_CONFIG.BG_WELCOME_ARENA.png}
          webp={BACKGROUNDS_CONFIG.BG_WELCOME_ARENA.webp}
          alt="welcome arena"
        />
      </div>
      <div className="flex items-center w-full flex-col px-6 pb-4 relative z-[2]">
        <H1 weight="extrabold" className="!text-2xl !leading-none mb-6">
          {t('The Arena is open!')}
        </H1>
        <ArenaBenefits />
        {tournamentPrizePool.usd > 0 && (
          <div className={css.banner}>
            <div className="w-full h-full flex items-center justify-between relative z-[2]">
              <div className="h-full flex flex-col justify-between">
                <div className="flex flex-col">
                  <H3 weight="bold" className="mb-2">
                    {t('Prize Pool')}
                  </H3>
                  <StringMain weight="extrabold" className="!text-[2.25rem]">
                    $ {tournamentPrizePool.usd}
                  </StringMain>
                </div>
                <StringMain className="!text-lg !text-white/60 !leading-1.2">
                  {t('Rewards every week!')}
                </StringMain>
              </div>
              <Picture
                className="w-32 h-32 shrink-0"
                img={usdPng}
                webp={usdWebp}
                alt="toncoins"
              />
            </div>
            <span
              style={{
                ...firstCircleSize,
                transform: calcAdaptivePositon({
                  originSize: firstCircleOriginSize,
                  originPosition: { x: -36, y: -60 },
                }),
              }}
              className={css.circleLeft}
            ></span>
            <Picture
              className={css.waves}
              img={wavesPng}
              webp={wavesWebp}
              alt="waves"
            />
            <span
              style={{
                ...secondCircleSize,
                transform: calcAdaptivePositon({
                  originSize: secondCircleOriginSize,
                  originPosition: { x: 17, y: 35 },
                }),
              }}
              className={css.circleRight}
            ></span>
          </div>
        )}
        <MainButton
          text={t('Join now!')}
          onClick={() => {
            closeModal();
            dispatch(endWelcomeArena());
            navigate(AppRoute.LEAGUES);
          }}
        />
      </div>
    </div>
  );
};

export default WelcomeArena;
